const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://app.translationflow.microdesk.space/api';

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
}

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.message || `HTTP ${response.status}`,
          message: data.message,
        };
      }

      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Authentication endpoints
  async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<{ token: string; user: any }>> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials: {
    email: string;
    password: string;
  }): Promise<ApiResponse<{ token: string; user: any }>> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async shopifyAuth(authData: {
    shop: string;
    accessToken: string;
    scope: string;
    userId: number;
    expiresIn: number;
    timestamp: number;
  }): Promise<ApiResponse<{ token: string; user: any }>> {
    return this.request('/auth/shopify', {
      method: 'POST',
      body: JSON.stringify(authData),
    });
  }

  async validateToken(): Promise<ApiResponse<any>> {
    return this.request('/auth/me');
  }

  // User management
  async getCurrentUser(): Promise<ApiResponse<any>> {
    return this.request('/auth/me');
  }

  async updateUser(userData: Partial<any>): Promise<ApiResponse<any>> {
    return this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Store management
  async connectStore(storeData: {
    shopifyDomain: string;
    shopifyAccessToken: string; // Updated to match backend
    shopifyShopName?: string;
  }): Promise<ApiResponse<any>> {
    return this.request('/stores/connect', {
      method: 'POST',
      body: JSON.stringify(storeData),
    });
  }

  async getStores(): Promise<ApiResponse<any[]>> {
    return this.request('/stores');
  }

  async getStore(storeId: string): Promise<ApiResponse<any>> {
    return this.request(`/stores/${storeId}`);
  }

  async updateStore(storeId: string, storeData: Partial<any>): Promise<ApiResponse<any>> {
    return this.request(`/stores/${storeId}`, {
      method: 'PUT',
      body: JSON.stringify(storeData),
    });
  }

  // Translation endpoints
  async getTranslationJobs(): Promise<ApiResponse<any[]>> {
    return this.request('/translations/jobs');
  }

  async createTranslationJob(jobData: {
    store_id: number;
    settings: any;
  }): Promise<ApiResponse<any>> {
    return this.request('/translations/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
  }

  async retryTranslationJobs(jobIds: number[]): Promise<ApiResponse<any>> {
    return this.request('/translations/jobs/retry', {
      method: 'POST',
      body: JSON.stringify({ job_ids: jobIds })
    });
  }

  async getTranslationJob(jobId: number): Promise<ApiResponse<any>> {
    return this.request(`/translations/jobs/${jobId}`);
  }

  async getJobProgress(jobId: number): Promise<ApiResponse<any>> {
    return this.request(`/translations/jobs/${jobId}/progress`);
  }

  async deleteTranslationJob(jobId: number): Promise<ApiResponse<any>> {
    return this.request(`/translations/jobs/${jobId}`, {
      method: 'DELETE',
    });
  }

  async getJobProgressStream(jobId: number): Promise<EventSource> {
    const token = localStorage.getItem('auth_token');
    const url = `${API_BASE_URL}/translations/jobs/${jobId}/progress/stream`;
    
    return new EventSource(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    } as any);
  }

  async getResourceTypes(): Promise<ApiResponse<string[]>> {
    return this.request('/translations/resource-types');
  }

  async getStoreLocales(storeId: number): Promise<ApiResponse<any>> {
    return this.request(`/translations/stores/${storeId}/locales`);
  }

  async testStoreConnection(storeId: number): Promise<ApiResponse<any>> {
    return this.request(`/translations/stores/${storeId}/test-connection`, {
      method: 'POST',
    });
  }

  // Subscription management (for future use)
  async getSubscription(): Promise<ApiResponse<any>> {
    return this.request('/billing/subscription');
  }

  async createSubscription(planData: any): Promise<ApiResponse<any>> {
    return this.request('/billing/subscription', {
      method: 'POST',
      body: JSON.stringify(planData),
    });
  }

  // Settings endpoints
  async getSettings(): Promise<ApiResponse<any>> {
    const result = await this.request('/settings');
    return result;
  }

  async saveSettings(settingsData: {
    openaiApiKey?: string;
    baseLanguage?: string;
    targetLanguages?: string[];
    translationPrompt?: string;
    selectedResourceTypes?: string[];
    maxTokens?: number;
    temperature?: number;
    autoTranslate?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request('/settings', {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  async testPrompt(testData: {
    prompt: string;
    text?: string;
    targetLanguage?: string;
    resourceType?: string;
    resourceId?: string;
  }): Promise<ApiResponse<{
    success: boolean;
    originalText: string;
    translatedText: string;
    targetLanguage: string;
    prompt: string;
  }>> {
    return this.request('/settings/test-prompt', {
      method: 'POST',
      body: JSON.stringify(testData),
    });
  }

  // Translation History endpoints
  async getTranslationHistory(params?: {
    job_id?: number;
    resource_type?: string;
    locale?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{
    history: any[];
    total: number;
    limit: number;
    offset: number;
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.job_id) queryParams.append('job_id', params.job_id.toString());
    if (params?.resource_type) queryParams.append('resource_type', params.resource_type);
    if (params?.locale) queryParams.append('locale', params.locale);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const endpoint = `/translations/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.request(endpoint);
  }

  async getTranslationHistoryEntry(entryId: number): Promise<ApiResponse<any>> {
    return this.request(`/translations/history/${entryId}`);
  }

  async getUserUsage(): Promise<ApiResponse<{ translations: number; languages: number }>> {
    return this.request('/translations/usage');
  }

  async checkSubscription(): Promise<ApiResponse<{
    store: {
      id: number;
      shopify_domain: string;
      plan_type: string;
      status: string;
      shopify_subscription_id: string | null;
      trial_ends_at: string | null;
    };
    subscription_active: boolean;
    subscription_details: any;
    requires_action: boolean;
    redirect_url: string | null;
  }>> {
    return this.request('/billing/check-subscription');
  }

  async fetchShopifyResources(resourceType: string): Promise<ApiResponse<{
    resources: Array<{id: string, title: string}>;
  }>> {
    return this.request(`/settings/fetch-resources?type=${resourceType}`);
  }
}

export const apiService = new ApiService();
export default apiService; 